import { useEffect, useState } from 'react'
import { useTranslation } from '@/i18n'
import { toast } from 'sonner'
import { Shield, AlertTriangle, ExternalLink } from 'lucide-react'
import { checkPdfJsCompatibility } from '@/utils/adBlockerDetection'
import type { CompatIssue } from '@/utils/adBlockerDetection'

interface AdBlockerWarningProps {
  onDetectionComplete?: (hasIssues: boolean) => void
  showToast?: boolean
  showInline?: boolean
}

const AD_BLOCKER_WARNING_KEY = 'adblocker-warning-dismissed'
const WARNING_EXPIRY_HOURS = 24

/**
 * Component that detects ad blockers and shows appropriate warnings for PDF.js compatibility
 */
export const AdBlockerWarning = ({
  onDetectionComplete,
  showToast = true,
  showInline = false,
}: AdBlockerWarningProps) => {
  const [detectionResult, setDetectionResult] = useState<{
    hasIssues: boolean
    adBlockerDetected: boolean
    issues: CompatIssue[]
  } | null>(null)
  const [showInlineWarning, setShowInlineWarning] = useState(false)
  const { t } = useTranslation()

  /**
   * Issue codes -> words. The detector reports facts; every string a reader
   * sees is resolved here, where `t()` exists (UI-13). `method` is a probe
   * name, so it is not itself translated.
   */
  const issueLabel = (issue: CompatIssue): string =>
    t(`common:ad_blocker.issue_${issue.code}`, {
      method: issue.method ?? '',
      defaultValue: issue.code,
    })

  const recommendationLabel = (issue: CompatIssue): string =>
    t(`common:ad_blocker.rec_${issue.code}`, { defaultValue: '' })

  useEffect(() => {
    const runDetection = async () => {
      try {
        const compatibility = await checkPdfJsCompatibility()

        setDetectionResult({
          hasIssues: !compatibility.compatible,
          adBlockerDetected: compatibility.adBlockerDetected,
          issues: compatibility.issues,
        })

        onDetectionComplete?.(!compatibility.compatible)

        // Show warnings if issues are detected
        if (!compatibility.compatible) {
          const lastWarning = localStorage.getItem(AD_BLOCKER_WARNING_KEY)
          const shouldShowWarning =
            !lastWarning ||
            Date.now() - parseInt(lastWarning) >
              WARNING_EXPIRY_HOURS * 60 * 60 * 1000

          if (shouldShowWarning) {
            if (showToast) {
              showAdBlockerToast(
                compatibility.adBlockerDetected,
                compatibility.issues
              )
            }

            if (showInline) {
              setShowInlineWarning(true)
            }

            // Store timestamp to avoid showing warning too frequently
            localStorage.setItem(AD_BLOCKER_WARNING_KEY, Date.now().toString())
          }
        }
      } catch (error) {
        console.warn('Ad blocker detection failed:', error)
        onDetectionComplete?.(false)
      }
    }

    runDetection()
  }, [onDetectionComplete, showToast, showInline])

  // `isAdBlocker` arrives as a fact from the detector. It used to be re-derived
  // here by substring-matching the English issue text, which breaks under
  // translation and let any unrelated issue print the ad-blocker headline.
  const showAdBlockerToast = (isAdBlocker: boolean, issues: CompatIssue[]) => {
    const firstRecommendation = issues
      .map(recommendationLabel)
      .find((text) => text.length > 0)

    toast.error(
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <span className="font-semibold">
            {isAdBlocker ? t('common:ad_blocker.ad_blocker_detected') : t('common:ad_blocker.pdf_viewer_issue')}
          </span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {isAdBlocker
            ? t('common:ad_blocker.ad_blocker_warning')
            : t('common:ad_blocker.pdf_viewer_compat_warning')}
        </div>
        {firstRecommendation && (
          <div className="text-sm">
            <strong>{t('common:ad_blocker.recommendation')}</strong> {firstRecommendation}
          </div>
        )}
      </div>,
      {
        duration: 8000,
        action: {
          label: t('common:ad_blocker.learn_more'),
          onClick: () => showDetailedHelp(issues),
        },
      }
    )
  }

  const showDetailedHelp = (issues: CompatIssue[]) => {
    const recommendations = issues
      .map(recommendationLabel)
      .filter((text) => text.length > 0)

    toast.info(
      <div className="flex flex-col gap-3 max-w-md">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-500" />
          <span className="font-semibold">{t('common:ad_blocker.pdf_viewer_help')}</span>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t('common:ad_blocker.issues_detected')}</div>
          <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
            {issues.map((issue) => (
              <li key={issue.code}>{issueLabel(issue)}</li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-sm font-medium mb-1">{t('common:ad_blocker.solutions')}</div>
          <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
            {recommendations.map((rec) => (
              <li key={rec}>{rec}</li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-gray-500 dark:text-dark-text-muted pt-2 border-t">
          {t('common:ad_blocker.persist_message')}
        </div>
      </div>,
      {
        duration: 12000,
      }
    )
  }

  const dismissInlineWarning = () => {
    setShowInlineWarning(false)
    localStorage.setItem(AD_BLOCKER_WARNING_KEY, Date.now().toString())
  }

  // Inline warning component
  if (showInline && showInlineWarning && detectionResult?.hasIssues) {
    const firstRecommendation = detectionResult.issues
      .map(recommendationLabel)
      .find((text) => text.length > 0)

    return (
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-1">
              {t('common:ad_blocker.compat_issue_title')}
            </h3>
            <p className="text-sm text-orange-700 dark:text-orange-400 mb-2">
              {detectionResult.adBlockerDetected
                ? t('common:ad_blocker.ad_blocker_warning')
                : t('common:ad_blocker.pdf_viewer_compat_warning')}
            </p>
            {firstRecommendation && (
              <p className="text-sm text-orange-700 dark:text-orange-400 mb-3">
                <strong>{t('common:ad_blocker.try')}</strong> {firstRecommendation}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => showDetailedHelp(detectionResult.issues)}
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 underline flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                {t('common:ad_blocker.learn_more')}
              </button>
              <button
                onClick={dismissInlineWarning}
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 underline ml-4"
              >
                {t('common:ad_blocker.dismiss')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default AdBlockerWarning
